import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MessageCircle, Phone, Send, Loader2, MapPin, Globe2 } from "lucide-react";
import { BRAND, MAILTO_URL, WHATSAPP_URL, MAPS_EMBED_URL, MAPS_LINK_URL } from "@/data/site";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const loadRecaptcha = (siteKey) =>
  new Promise((resolve) => {
    if (!siteKey) return resolve(null);
    if (window.grecaptcha && window.grecaptcha.execute) {
      return window.grecaptcha.ready(() => resolve(window.grecaptcha));
    }
    const finish = () =>
      window.grecaptcha ? window.grecaptcha.ready(() => resolve(window.grecaptcha)) : resolve(null);
    const existing = document.querySelector("script[data-recaptcha]");
    if (existing) {
      existing.addEventListener("load", finish);
      existing.addEventListener("error", () => resolve(null));
      // în caz că s-a încărcat deja între timp
      if (window.grecaptcha) finish();
      return;
    }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.setAttribute("data-recaptcha", "1");
    s.onload = finish;
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-lg border border-white/10 bg-ink-900/70 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-200 focus:border-cyan-neon/60";

export const Contact = () => {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Cerere colaborare", message: "", website: "", source: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRecaptcha(RECAPTCHA_SITE_KEY).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { subject, message, source } = e.detail || {};
      setForm((f) => ({
        ...f,
        subject: subject || f.subject,
        message: message || f.message,
        source: source || f.source,
      }));
    };
    window.addEventListener("dmo:prefill-contact", handler);
    return () => window.removeEventListener("dmo:prefill-contact", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error(t("ct.err.fields"));
      return;
    }
    setLoading(true);
    try {
      let recaptcha_token = null;
      if (RECAPTCHA_SITE_KEY) {
        const grecaptcha = await loadRecaptcha(RECAPTCHA_SITE_KEY);
        if (grecaptcha) {
          try {
            recaptcha_token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: "contact_submit" });
          } catch {
            recaptcha_token = null;
          }
        }
      }
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptcha_token }),
      });
      if (res.status === 429) {
        toast.error(t("ct.err.rate"));
        return;
      }
      if (res.status === 400) {
        toast.error(t("ct.err.captcha"));
        return;
      }
      if (!res.ok) throw new Error("bad status");
      await res.json();
      toast.success(t("ct.ok"));
      setForm({ name: "", email: "", phone: "", subject: "Cerere colaborare", message: "", website: "", source: "" });
    } catch {
      toast.error(t("ct.err.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden border-t border-white/10 bg-ink-900"
    >
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="absolute left-1/4 top-1/2 -z-0 h-[30rem] w-[30rem] -translate-y-1/2 rounded-full bg-cyan-neon/10 blur-[160px]" />

      <div className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <Reveal><SectionLabel index="[ 06 ]">{t("ct.label")}</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display text-[15vw] font-extrabold leading-[0.9] tracking-tighter text-white sm:text-[11vw] lg:text-[9rem]">
            {t("ct.h1a")} <span className="text-aurora text-aurora-glow">{t("ct.h1b")}</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Left */}
          <div>
            <Reveal delay={0.1}>
              <p className="max-w-md font-body text-base leading-relaxed text-white/60 sm:text-lg">
                {t("ct.p")}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  data-testid="contact-whatsapp"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-shine inline-flex items-center justify-center gap-3 bg-cyan-neon px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black shadow-[0_0_30px_rgba(0,229,255,0.35)] transition-colors duration-300 hover:bg-white"
                >
                  <MessageCircle size={18} /> WhatsApp
                </a>
                <a
                  data-testid="contact-email"
                  href={MAILTO_URL}
                  className="inline-flex items-center justify-center gap-3 border border-white/20 bg-white/5 px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-white backdrop-blur-md transition-colors duration-300 hover:border-cyan-neon hover:text-cyan-neon"
                >
                  <Mail size={18} /> Email
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2">
                <a href={MAILTO_URL} data-testid="contact-detail-email" className="group bg-ink-800 p-6 transition-colors duration-300 hover:bg-ink-700">
                  <Mail size={18} className="text-cyan-neon" />
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ct.detail.email")}</div>
                  <div className="mt-1 break-all font-display text-sm font-semibold text-white">{BRAND.email}</div>
                </a>
                <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="contact-detail-phone" className="group bg-ink-800 p-6 transition-colors duration-300 hover:bg-ink-700">
                  <Phone size={18} className="text-cyan-neon" />
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ct.detail.phone")}</div>
                  <div className="mt-1 font-display text-sm font-semibold text-white">{BRAND.phone}</div>
                </a>
                <a href={MAPS_LINK_URL} target="_blank" rel="noreferrer" data-testid="contact-detail-location" className="group bg-ink-800 p-6 transition-colors duration-300 hover:bg-ink-700">
                  <MapPin size={18} className="text-cyan-neon" />
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ct.detail.location")}</div>
                  <div className="mt-1 font-display text-sm font-semibold text-white">{t("ct.location.value")}</div>
                </a>
                <div data-testid="contact-detail-coverage" className="bg-ink-800 p-6">
                  <Globe2 size={18} className="text-cyan-neon" />
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ct.coverage.label")}</div>
                  <div className="mt-1 font-display text-sm font-semibold text-white">{t("ct.coverage.value")}</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
                <iframe
                  data-testid="contact-map"
                  title={t("ct.map.aria")}
                  src={MAPS_EMBED_URL}
                  className="h-64 w-full grayscale-[0.4] invert-[0.92] hue-rotate-180 contrast-[0.9]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <a
                  href={MAPS_LINK_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-map-link"
                  className="flex items-center justify-center gap-2 border-t border-white/10 bg-ink-800 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-cyan-neon"
                >
                  <MapPin size={13} /> {t("ct.map.open")}
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right — form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="card-edge rounded-2xl p-6 sm:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t("ct.f.name")}>
                <input data-testid="form-name" className={inputCls} value={form.name} onChange={set("name")} placeholder={t("ct.f.namePh")} />
              </Field>
              <Field label={t("ct.f.email")}>
                <input data-testid="form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder={t("ct.f.emailPh")} />
              </Field>
              <Field label={t("ct.f.phone")}>
                <input data-testid="form-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder={t("ct.f.phonePh")} />
              </Field>
              <Field label={t("ct.f.subject")}>
                <input data-testid="form-subject" className={inputCls} value={form.subject} onChange={set("subject")} placeholder={t("ct.f.subjectPh")} />
              </Field>
            </div>
            <div className="mt-4">
              <Field label={t("ct.f.message")}>
                <textarea data-testid="form-message" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder={t("ct.f.messagePh")} />
              </Field>
            </div>
            {/* Honeypot anti-spam — ascuns pentru utilizatori reali, completat doar de boți */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
              <label>
                Website
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                />
              </label>
            </div>
            <button
              data-testid="form-submit"
              type="submit"
              disabled={loading}
              className="btn-shine mt-6 inline-flex w-full items-center justify-center gap-2 bg-cyan-neon px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.1em] text-black shadow-[0_0_30px_rgba(0,229,255,0.3)] transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <><Loader2 size={17} className="animate-spin" /> {t("ct.f.sending")}</> : <><Send size={17} /> {t("ct.f.send")}</>}
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-white/30">
              {t("ct.f.reply")}
            </p>
            <p className="mt-1.5 text-center font-body text-[10px] leading-relaxed text-white/25">
              {t("ct.recaptcha1")}{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-white/40">{t("ct.recaptcha.privacy")}</a>{" "}{t("ct.recaptcha.and")}{" "}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-white/40">{t("ct.recaptcha.terms")}</a>{" "}{t("ct.recaptcha.google")}
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
